import { type GridApiPro, useGridApiRef } from '@mui/x-data-grid-pro';
import React, { useMemo } from 'react';
import type { GetPaymentsNewResponse } from '@vy/api-definition';
import { TABLE_NAMES } from '@vy/types';
import type { ItemOf } from '@vy/utils';
import { flattenCustomFieldValuesIntoRow } from '@vy/custom-fields-display';
import type { VyTableMinimalProps } from '../../../components/new-design/table/vy-table/vy-table-types';
import { VyTableWithFilters } from '../../../components/new-design/table/vy-table-with-filters';
import { getSelectorAttribute, paymentsPageSelectors } from '@vy/app-selectors';
import { PAYMENTS_COLUMNS_KEYS } from './payment-columns';
import { usePaymentsFilters } from './payments-page-hooks/use-payments-filters';
import { PAYMENTS_FILTER_FIELDS } from './payments-page-utils/payment-filter-fields';
import { usePaymentsActions } from './payments-page-hooks/use-payments-actions';
import { usePaymentsTableColumns } from './payments-page-hooks/use-payments-table-columns';
import { VyRangePicker } from '../../../components/new-design/inputs/VyRangePicker';

type PaymentsTableProps = Omit<VyTableMinimalProps<ItemOf<GetPaymentsNewResponse['items']>>, 'tableName'> & {
  filters?: ReturnType<typeof usePaymentsFilters>;
};

export const PaymentsOverviewTable: React.FC<PaymentsTableProps> = ({ filters: filtersProp, items, apiRef: _apiRefProp, ...props }) => {
  const apiRef = useGridApiRef();
  const filtersFromHook = usePaymentsFilters();
  const filters = filtersProp ?? filtersFromHook;

  const rowsWithFlattenedCustomFields = useMemo(
    () => (items ?? []).map(flattenCustomFieldValuesIntoRow),
    [items],
  );

  const columns = usePaymentsTableColumns({
    tableName: TABLE_NAMES.PAYMENTS,
    items,
  });

  const { handleExportSelectedPayments } = usePaymentsActions({
    selectedPayments: [],
    apiRef,
  });

  return (
    <VyTableWithFilters
      {...getSelectorAttribute(paymentsPageSelectors.table)}
      apiRef={apiRef as unknown as React.MutableRefObject<GridApiPro>}
      tableName={TABLE_NAMES.PAYMENTS}
      columns={columns}
      items={rowsWithFlattenedCustomFields}
      dateRangePicker={
        <VyRangePicker
          size="small"
          textFieldProps={{
            sx: {
              '& .MuiFilledInput-root': {
                height: '40px',
                backgroundColor: '#F5F6FA',
                borderRadius: '4px',
              },
            },
          }}
        />
      }
      search={{
        value: filters.query ?? '',
        onChange: filters.handleSearchInputChange,
        placeholder: 'Search by Customer, Invoice, Reference...',
      }}
      filters={{
        fields: PAYMENTS_FILTER_FIELDS,
        onFiltersChange: filters.handleFilterClick,
        defaultFilters: filters.filters,
      }}
      columnSettings={{
        excludedFields: [PAYMENTS_COLUMNS_KEYS.DOT_MENU],
        mandatoryColumnsCount: 2,
      }}
      onDownload={handleExportSelectedPayments}
      isFilterOpen={filters.isFilterPaymentsOpen}
      disableRowSelectionOnClick
      rowsPerPageOptions={[10, 25, 50, 100, 500]}
      disableColumnMenu
      getRowId={(row) => row.id}
      {...props}
    />
  );
};
